import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { addDays, addWeeks, format } from "date-fns";
import { DATE_DAY_FROM_MONTH } from "@/services/formatters";

const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

type MaterialSummary = {
	brand?: string | null;
	model?: string | null;
	description?: string | null;
	price?: number | null;
};

type PurchaseEntry = {
	id?: string | number | null;
	material?: MaterialSummary | null;
	quantity?: number | null;
	origin?: string | null;
	createdAt?: string | Date | null;
	link?: string | null;
	price?: number | null;
	state?: string | null;
};

type DashboardPurchaseTableProps = {
	purchases?: PurchaseEntry[];
	loading?: boolean;
	onConfirm: (purchase: PurchaseEntry) => void;
	onCancel: (purchase: PurchaseEntry) => void;
};

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

const estimateDelivery = (purchase: PurchaseEntry) => {
	const createdAt = purchase.createdAt ? new Date(purchase.createdAt) : null;
	if (!createdAt || Number.isNaN(createdAt.getTime())) {
		return "-";
	}

	if (purchase.origin === "europe") {
		const end = addDays(createdAt, 4);
		return `(${format(createdAt, DATE_DAY_FROM_MONTH)} - ${format(
			end,
			DATE_DAY_FROM_MONTH,
		)})`;
	}

	const start = addWeeks(createdAt, 2);
	const end = addWeeks(createdAt, 6);
	return `(${format(start, DATE_DAY_FROM_MONTH)} - ${format(
		end,
		DATE_DAY_FROM_MONTH,
	)})`;
};

const formatOrigin = (origin?: string | null) => {
	if (origin === "europe") {
		return "Europa";
	}
	if (origin === "other") {
		return "Fora da Europa";
	}
	return "-";
};

export function DashboardPurchaseTable({
	purchases = [],
	loading = false,
	onConfirm,
	onCancel,
}: DashboardPurchaseTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`purchase-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-48 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-10 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/30" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-24 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead>Descrição</TableHead>
						<TableHead className="w-[70px] text-center">Qtn.</TableHead>
						<TableHead className="w-[100px] text-center">Origem</TableHead>
						<TableHead className="w-[250px] text-center">Compra</TableHead>
						<TableHead className="w-[250px] text-center">Previsão</TableHead>
						<TableHead className="w-[70px] text-center">Link</TableHead>
						<TableHead className="w-[100px] text-center">Preço</TableHead>
						<TableHead className="w-[200px] text-right" />
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : (
					<TableBody>
						{purchases.map((purchase) => {
							const rowKey = purchase.id ?? "purchase-row";
							const material = purchase.material;
							const description = material
								? `${material.brand ?? ""} ${material.model ?? ""}, ${
										material.description ?? ""
									}`.trim()
								: "-";
							const price = purchase.price ?? material?.price ?? 0;

							return (
								<TableRow
									key={rowKey}
									className={`tp-Purchases__item is-${purchase.state ?? ""}`}
								>
									<TableCell>{description}</TableCell>
									<TableCell className="text-center">
										{purchase.quantity ?? 0}
									</TableCell>
									<TableCell className="text-center">
										{formatOrigin(purchase.origin)}
									</TableCell>
									<TableCell className="text-center">
										{purchase.createdAt
											? format(new Date(purchase.createdAt), "dd/MM/yy")
											: "-"}
									</TableCell>
									<TableCell className="text-center">
										{estimateDelivery(purchase)}
									</TableCell>
									<TableCell className="text-center">
										{purchase.link ? (
											<a href={purchase.link} target="_blank" rel="noreferrer">
												Link
											</a>
										) : (
											"-"
										)}
									</TableCell>
									<TableCell className="text-center">
										{formatCurrency(normalizeNumber(price))}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onConfirm(purchase)}
											>
												Efetivar
											</Button>
											<Button
												size="sm"
												variant="destructive"
												onClick={() => onCancel(purchase)}
											>
												Apagar
											</Button>
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				)}
			</Table>
		</div>
	);
}
